import { Alert, Button, Tab, Tabs } from '@components'
import { TagDisplay } from '@components/TagDisplay/TagDisplay'
import { DocumentBankDetails } from '@containers/DocumentBankDetails'
import { getContactPageUrl } from '@constants'
import { PermissionSet, useBusinessContext, usePermission } from '@context'
import { bankIntegrationDataSources, IDocument, ImportDataSource, setFiles } from '@query'
import { DocumentAttachmentsField } from '@pages/DocumentPage/DocumentForm/DocumentAttachmentsField'
import { formatDate, selectUserNotificationIcon } from '@utils'
import dayjs from 'dayjs'
import { motion } from 'framer-motion'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaInfoCircle, FaSyncAlt } from 'react-icons/fa'
import { FaMapLocationDot } from 'react-icons/fa6'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useHistory } from 'react-router-dom'
import styled from 'styled-components'
import { AttachmentSuggestions } from './AttachmentSuggestions'
import RelationMapper from '@pages/DocumentPage/DocumentForm/RelationMapper'
import {
  getDocumentRelationContext,
  mapRelationsForForm
} from '@pages/DocumentPage/DocumentForm/relationMapperUtils'
import { DocumentAuditTrail } from './DocumentAuditTrail'
import { EntryOverview } from './EntryOverview'
import { useAllowUpload } from '@root/hooks/useAllowUpload'
import { AccountingSuggestions } from '@pages/DocumentPage/DocumentDetails/AccountingSuggestions'
import { SourceInvoiceDocumentSection, useSourceInvoiceFetch } from './SourceInvoiceWidget'
import { isAccrualInvoiceImportSource, showsImportSourceTab } from './invoiceImportUtils'
import {
  RelationLead,
  RelationsHeader
} from '@pages/DocumentPage/DocumentForm/RelationMapper.styled'

interface Props {
  document: IDocument
  setShowEdit: (value: boolean) => void
}

const showsAccountingSuggestionCard = (document: IDocument): boolean =>
  Boolean(
    document.is_draft &&
      document.import_data &&
      (bankIntegrationDataSources.includes(document.import_data.data_source) ||
        document.import_data.data_source === ImportDataSource.SALAXY ||
        document.import_data.data_source === ImportDataSource.WORKFLOW)
  )

const isBankImportDraftDocument = (document: IDocument): boolean =>
  Boolean(
    document.is_draft &&
      document.import_data &&
      bankIntegrationDataSources.includes(document.import_data.data_source)
  )

export const DocumentOverview: React.FC<Props> = ({ document, setShowEdit }) => {
  const [t] = useTranslation()
  const history = useHistory()
  const { slug } = useBusinessContext()
  const period = dayjs(document.date).format('M/YYYY')
  const hasContact = Boolean(document.contact_id)

  return (
    <Tabs>
      <Tab key="overview" icon={<FaInfoCircle />} name={t('document.tab.details')}>
        {document.eu_sale_to_private_person ? (
          <Alert
            type="warning"
            title={t('document.euPrivatePersonTitle')}
            description={
              <>
                {t('document.euPrivatePersonDetails')}
                <AlertAction>
                  <Button intent="primary" onClick={() => setShowEdit(true)}>
                    {t('document.euB2bVatEditDocument')}
                  </Button>
                </AlertAction>
              </>
            }
          />
        ) : document.eu_b2b_vat_details_missing ? (
          <Alert
            type="warning"
            title={t(
              hasContact
                ? 'document.acceptEuB2bMissingVatIdConfirmTitle'
                : 'document.acceptEuB2bMissingContactConfirmTitle'
            )}
            description={
              <>
                {hasContact
                  ? t('document.euB2bVatDetailsMissingVatId', { period })
                  : t('document.euB2bVatDetailsMissingContact', { period })}
                <AlertAction>
                  {hasContact && document.contact_id ? (
                    <Button
                      intent="primary"
                      onClick={() =>
                        history.push(getContactPageUrl(slug, { id: document.contact_id }))
                      }
                    >
                      {t('document.euB2bVatEditContact')}
                    </Button>
                  ) : (
                    <Button intent="primary" onClick={() => setShowEdit(true)}>
                      {t('document.euB2bVatEditDocument')}
                    </Button>
                  )}
                </AlertAction>
              </>
            }
          />
        ) : null}
        {showsAccountingSuggestionCard(document) ? (
          <AccountingSuggestions
            document={document}
            emptyContent={<DocumentOverviewDetails document={document} setShowEdit={setShowEdit} />}
          />
        ) : (
          <DocumentOverviewDetails document={document} setShowEdit={setShowEdit} />
        )}
      </Tab>

      {document.import_data && showsImportSourceTab(document.import_data) && (
        <Tab key="import_data" icon={<FaSyncAlt />} name={t('document.importSource.tabName')}>
          <DocumentBankDetails import_data={document.import_data} />
        </Tab>
      )}

      <Tab
        key="audit_trail"
        icon={<FaMapLocationDot />}
        name={t('components.auditTrail.tabHeader')}
        notification_dot={selectUserNotificationIcon(document)}
      >
        <DocumentAuditTrail document={document}></DocumentAuditTrail>
      </Tab>
    </Tabs>
  )
}

const DocumentOverviewDetails: React.FC<Props> = ({ document, setShowEdit }) => {
  const [t] = useTranslation()
  const { slug } = useBusinessContext()
  const isAllowedToUpload = useAllowUpload()
  const hasEditPermission = usePermission(PermissionSet.BOOKKEEPING_EDIT)
  const hasBookkeepingFilesPermission = usePermission(PermissionSet.BOOKKEEPING_FILES)

  const hideFileSection = document.attachment_ids?.length === 0 && !hasBookkeepingFilesPermission
  const isBankImportDraft = isBankImportDraftDocument(document)
  const showDraftLedger = !document.is_draft || !isBankImportDraft

  const queryClient = useQueryClient()

  const invalidateDocuments = async () => {
    await queryClient.invalidateQueries({ queryKey: [slug, 'documents'] })
  }

  const attachFilesMutation = useMutation<unknown, unknown, number[]>({
    mutationFn: ids => setFiles({ slug, documentId: document.id }, ids),
    onSuccess: invalidateDocuments
  })

  const isInvoiceSourcedDocument = isAccrualInvoiceImportSource(document.import_data?.data_source)
  const sourceInvoiceFetch = useSourceInvoiceFetch(document)

  return (
    <div>
      {document.number && (
        <DetailRow>
          <StyledHeader>{t('document.number')}</StyledHeader>
          <StyledValue data-test="document-overview-number">{document.number}</StyledValue>
        </DetailRow>
      )}

      <DetailRow>
        <StyledHeader>{t('document.date')}</StyledHeader>
        <StyledValue data-test="document-overview-date">{formatDate(document.date)}</StyledValue>
      </DetailRow>

      {document.contact_id && (
        <DetailRow>
          <StyledHeader>{t('document.contact')}</StyledHeader>
          <StyledValue>
            <Link
              to={`/${slug}/contacts?id=${document.contact_id}`}
              data-test="document-overview-contact"
            >
              {document.contact_name}
            </Link>
          </StyledValue>
        </DetailRow>
      )}

      {!document.contact_id && document.import_data?.contact_hint && (
        <DetailRow>
          <StyledHeader>{t('document.contact')}</StyledHeader>
          <StyledValue>{document.import_data?.contact_hint}</StyledValue>
        </DetailRow>
      )}

      <DetailRow>
        <StyledHeader>{t('document.description')}</StyledHeader>
        <StyledValue data-test="document-overview-description">
          {document.description || '-'}
        </StyledValue>
      </DetailRow>

      {document?.tag_ids?.length !== 0 && (
        <DetailRow>
          <TagDisplay
            tagIds={document.tag_ids}
            label={<StyledHeader>{t('document.tags')}</StyledHeader>}
          />
        </DetailRow>
      )}

      {showDraftLedger && (
        <DetailContainer>
          <StyledHeader>{t('document.entries')}</StyledHeader>
          <EntryOverview documentId={document.id} />
        </DetailContainer>
      )}

      <SourceInvoiceDocumentSection document={document} fetchState={sourceInvoiceFetch} />

      {document.relations?.length || !document.is_locked ? (
        <DetailContainer>
          <RelationMapper
            value={mapRelationsForForm(document.relations)}
            onChange={() => undefined}
            documentId={document.id}
            readOnly
            suggestionsEnabled={!document.is_locked}
            canApplySuggestion={hasEditPermission}
            renderHeader={
              isInvoiceSourcedDocument && sourceInvoiceFetch.showSection
                ? ({ addMenu, lead, themeMode }) => (
                    <RelationsHeader $themeMode={themeMode}>
                      {lead ? (
                        <RelationLead $themeMode={themeMode}>{lead}</RelationLead>
                      ) : (
                        <StyledHeader as="span">
                          {t('documentRelations.accrualSectionTitle')}
                        </StyledHeader>
                      )}
                      {addMenu}
                    </RelationsHeader>
                  )
                : undefined
            }
            {...getDocumentRelationContext(document)}
          />
        </DetailContainer>
      ) : null}

      {showDraftLedger && !hideFileSection && (
        <DetailContainer className="last">
          <StyledHeader>{t('document.attachments')}</StyledHeader>
          <DocumentAttachmentsField
            attachmentIds={document.attachment_ids}
            onAttachmentIdsChange={values => {
              void attachFilesMutation.mutateAsync(values)
            }}
            attachmentExempt={document.attachment_exempt}
            disabled={document.is_locked || !isAllowedToUpload || !hasBookkeepingFilesPermission}
            showUndo={false}
            showExempt={false}
          />
        </DetailContainer>
      )}

      {/* Show suggestions if no attachments */}
      {document.attachment_ids?.length === 0 && !document.attachment_exempt && (
        <AttachmentSuggestions document={document} />
      )}

      {document.is_draft && hasEditPermission && (
        <Alert
          description={
            <>
              <p>
                {t(
                  isBankImportDraft
                    ? 'document.draftDescription'
                    : 'document.draftDescriptionFilled'
                )}
              </p>
              <Button intent="primary" onClick={() => setShowEdit(true)}>
                {t('document.recordDocument')}
              </Button>
            </>
          }
        />
      )}
    </div>
  )
}

const DetailRow = styled.div`
  flex: 1;
  margin-bottom: ${({ theme }) => theme.spacing.lg}rem;
`

const DetailContainer = styled(motion.div).attrs({ layout: 'position' })`
  margin-bottom: ${({ theme }) => theme.spacing.lg}rem;

  &.last {
    border-bottom: none;
  }
`

const StyledHeader = styled.p`
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  margin-bottom: ${({ theme }) => theme.spacing.xs}rem;
  align-self: flex-start;
`

const StyledValue = styled.div`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  color: ${({ theme }) => theme.colors.neutralBlack};
  margin: 0;
`

const AlertAction = styled.div`
  margin-top: ${({ theme }) => theme.spacing.sm}rem;
`
